import { useCallback, useEffect, useRef, useState } from 'react';
import type { DashboardAggregatePayload } from '../../shared/dashboardAggregatePayload';
import { ApiRequestError } from '../api/apiRequestError';
import { getDashboardAggregate } from '../api/getDashboardAggregate';
import { postTestMode } from '../api/postTestMode';
import { ReactionTimelineChart } from '../charts/reactionTimelineChart';
import { RetentionCurveChart } from '../charts/retentionCurveChart';
import { DashboardReplayPlayer } from '../dashboard/dashboardReplayPlayer';
import { TopMomentsTable } from '../dashboard/topMomentsTable';

// Spec §5: below this, the shape is noise. Say so rather than let a creator read
// patterns into four people.
const MIN_REVIEWERS_FOR_PATTERNS = 8;

// Spec §12 leaves bucket size open on purpose — 5s is a starting guess and the
// right answer is unknown until a real dataset exists. Exposed as a control so it
// can be tuned against real data without a redeploy.
const BUCKET_OPTIONS = [2000, 5000, 10000, 15000];

const CHART_WIDTH = 900;

// Days remaining, rounded up: "0 days left" on the final day would read as
// already gone.
function daysUntil(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

// The deletion warning for an anonymous test, with the one action that clears it.
function ExpiryNotice(input: { expiresAt: string }) {
  const days = daysUntil(input.expiresAt);
  const when = new Date(input.expiresAt).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long'
  });
  return (
    <div className="calloutDanger">
      <b>This test will be deleted on {when}</b> — {days} {days === 1 ? 'day' : 'days'} from now,
      along with every reaction on it. The clock resets whenever someone new reacts.
      <p style={{ margin: '10px 0 0' }}>
        <a className="buttonSecondary buttonLink" href="/my">
          Sign in to keep it permanently
        </a>
      </p>
    </div>
  );
}

// The creator dashboard. Deliberately dumb (spec §1): raw curves, minimal derived
// metrics, no cleverness. The first real dataset may look nothing like we expect,
// and a dumb dashboard is cheap to replace.
export function CreatorDashboardScreen(input: { dashboardKey: string }) {
  const [data, setData] = useState<DashboardAggregatePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bucketMs, setBucketMs] = useState(5000);
  const [mode, setMode] = useState<'family' | 'icon'>('family');
  const seekRef = useRef<((ms: number) => void) | null>(null);
  const [modeBusy, setModeBusy] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const payload = await getDashboardAggregate(input.dashboardKey, bucketMs);
        if (!cancelled) setData(payload);
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof ApiRequestError ? caught.message : 'Could not load the dashboard.');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [input.dashboardKey, bucketMs]);

  const onPlayerReady = useCallback((seekToMs: (ms: number) => void) => {
    seekRef.current = seekToMs;
  }, []);

  if (error) {
    return (
      <main className="pageWrap">
        <h1>Can't open this dashboard</h1>
        <p className="pageSub">{error}</p>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pageWrap">
        <p className="pageSub">Loading…</p>
      </main>
    );
  }

  const durationMs = data.duration_ms ?? 0;

  return (
    <main className="pageWrap pageWrapWide">
      <h1>{data.title ?? 'Your test'}</h1>
      <p className="pageSub">
        {data.reviewer_count} {data.reviewer_count === 1 ? 'reviewer' : 'reviewers'} ·{' '}
        {data.finished_pct}% finished watching
      </p>

      {/* Criterion 5. A creator reading a spike needs to know whether they are
          looking at eleven independent reactions or one reaction and ten echoes.
          Stated next to the reviewer count, not buried. */}
      <div className={data.mode === 'live' ? 'modeBanner modeBannerLive' : 'modeBanner'}>
        <b>{data.mode === 'live' ? 'Live' : 'Blind'}</b>
        {data.mode === 'live'
          ? " — reviewers saw everyone else's reactions while they watched. Treat a spike as a shared moment, not as independent signal."
          : ' — each reviewer watched without seeing anyone else. Their reactions are independent.'}
        {' '}
        <button
          className="linkButton"
          type="button"
          disabled={modeBusy}
          onClick={() => {
            setModeBusy(true);
            setModeError(null);
            void postTestMode(input.dashboardKey, data.mode === 'live' ? 'blind' : 'live')
              .then(() => setData({ ...data, mode: data.mode === 'live' ? 'blind' : 'live' }))
              .catch((caught) =>
                setModeError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not change the mode.'
                )
              )
              .finally(() => setModeBusy(false));
          }}
        >
          {modeBusy ? 'Changing…' : `Switch to ${data.mode === 'live' ? 'blind' : 'live'}`}
        </button>
        <span className="mutedNote"> — applies to reviewers who start from now on.</span>
      </div>

      {/* Cowork020 §2. The mode lock used to make this state impossible; now the
          state is possible and disclosed instead. Shown only when BOTH regimes are
          actually present, because a notice that appears on every single-regime
          test is a notice nobody reads on the one test that needed it. */}
      {data.reviewers_blind > 0 && data.reviewers_live > 0 ? (
        <div className="calloutWarn">
          <b>
            {data.reviewer_count} {data.reviewer_count === 1 ? 'reviewer' : 'reviewers'} —{' '}
            {data.reviewers_blind} watched blind, {data.reviewers_live} watched live.
          </b>{' '}
          Reactions collected under different conditions are not directly comparable. The
          live reviewers could see what everyone before them had already felt; the blind
          ones could not. Everything below mixes both.
        </div>
      ) : null}
      {modeError ? <div className="calloutError">{modeError}</div> : null}

      {/* Criterion 14 / spec §9.3. An anonymous creator holds the only link and
          gets no email — if the test vanishes silently they lose everything with
          no warning. Shown plainly, not as fine print. Owned tests show none of
          this, because they never expire. */}
      {!data.is_owned && data.expires_at ? <ExpiryNotice expiresAt={data.expires_at} /> : null}

      {data.reviewer_count < MIN_REVIEWERS_FOR_PATTERNS ? (
        <div className="callout">
          <b>Too few reviewers to read patterns yet.</b> Aim for 10–15. Below that, one
          person's timing looks like a trend. The fastest way there is not to launch a
          link — it is to get everyone watching in the same hour.
        </div>
      ) : null}

      <section className="dashboardSection">
        <h2>Who was still watching</h2>
        <RetentionCurveChart
          points={data.retention}
          durationMs={durationMs}
          reviewerCount={data.reviewer_count}
          width={CHART_WIDTH}
          height={150}
        />
      </section>

      <section className="dashboardSection">
        <div className="sectionHeaderRow">
          <h2>What they felt</h2>
          <div className="controlGroup">
            <button
              type="button"
              className={mode === 'family' ? 'toggleOn' : ''}
              onClick={() => setMode('family')}
            >
              Families
            </button>
            <button
              type="button"
              className={mode === 'icon' ? 'toggleOn' : ''}
              onClick={() => setMode('icon')}
            >
              Individual icons
            </button>
          </div>
        </div>

        {/* Same x-domain and padding as the curve above (chartGeometry), so the
            two read together: where people left, and why. */}
        <ReactionTimelineChart
          buckets={data.buckets}
          icons={data.icons}
          durationMs={durationMs}
          bucketMs={data.bucket_ms}
          mode={mode}
          width={CHART_WIDTH}
          height={170}
        />

        <div className="controlGroup controlGroupSpaced">
          <span className="controlLabel">Bucket</span>
          {BUCKET_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={bucketMs === option ? 'toggleOn' : ''}
              onClick={() => setBucketMs(option)}
            >
              {option / 1000}s
            </button>
          ))}
        </div>
      </section>

      <section className="dashboardSection">
        <h2>Top moments</h2>
        <TopMomentsTable
          buckets={data.buckets}
          icons={data.icons}
          bucketMs={data.bucket_ms}
          onSeek={(ms) => seekRef.current?.(ms)}
        />
      </section>

      <section className="dashboardSection">
        <h2>Watch it back</h2>
        <DashboardReplayPlayer
          dashboardKey={input.dashboardKey}
          youtubeId={data.youtube_id}
          icons={data.icons}
          onPlayerReady={onPlayerReady}
        />
      </section>

      <p className="mutedNote">
        Reactions are anonymous by design. You are seeing the combined shape across
        everyone and there is no view that breaks it down by person — that is what
        makes the unflattering taps worth trusting.
      </p>
    </main>
  );
}
